import { Container, Graphics, Sprite, Text, Ticker, type Texture } from 'pixi.js';
import { AudioBus } from '../../audio/AudioBus';
import { TakeWinAnimation } from '../../effects/TakeWinAnimation';
import { animate, COLOURS, displayStyle, ease, prefersReducedMotion, uiStyle } from '../board/ui';
import { glowTexture } from '../road/glow';

/** The refill's painted pieces: the coin, and the chest of coins (the
 *  hold-and-spin chest). */
export interface RefillArt {
  (name: 'coin' | 'chest'): Texture | null;
}

export interface RefillPlay {
  /** The balance before the refill (almost always 0). */
  from: number;
  /** What the refill added. */
  amount: number;
  /** Where the coin counter is (screen pixels), for the coins to fly
   *  into at the end; null if there's none on screen. */
  target: () => { x: number; y: number } | null;
}

/** The refill moment as plain data, for browser tests. */
export interface RefillSnapshot {
  playing: boolean;
  counter: string;
  line: string;
  tagline: string;
}

interface Falling {
  sprite: Sprite;
  vx: number;
  vy: number;
  spin: number;
  age: number;
  life: number;
}

const TOTAL_MS = 4200;
const COUNTER_AT = 400;
const COUNTER_MS = 1200;
const LINE_AT = 1600;
const TAGLINE_AT = 2400;
const FLY_AT = 2900;
const FADE_AT = 3700;
const COINS = 70;
const GRAVITY = 900;

const format = (n: number): string => Math.round(n).toLocaleString('en-GB');

/**
 * The free refill (producer: the brand's promise, "all the spin, none of
 * the sting"): when a player runs out, the game tops them up for free. The
 * screen dims warm, a chest of coins bursts open and showers coins, the
 * counter rolls up to the new balance, the line says it's on the house,
 * and the coins fly into the coin counter as it clears. Drawn on the
 * overlay canvas, over any screen; it takes no taps.
 */
export class RefillCelebration extends Container {
  private readonly dim = new Graphics();
  private readonly glow = new Sprite(glowTexture());
  private readonly chest = new Sprite();
  private readonly shower = new Container();
  private readonly counter: Text;
  private readonly coinIcon = new Sprite();
  private readonly line: Text;
  private readonly tagline: Text;
  private readonly fly: TakeWinAnimation;
  private falling: Falling[] = [];
  private playing = false;
  private w = 1;
  private h = 1;
  private readonly tick = (ticker: Ticker): void => this.step(ticker.deltaMS);

  constructor(private readonly art: RefillArt) {
    super();
    this.glow.anchor.set(0.5);
    this.glow.tint = 0xffb86e;
    this.chest.anchor.set(0.5, 0.62);
    this.counter = new Text({ text: '0', style: displayStyle(72) });
    this.counter.anchor.set(0, 0.5);
    this.coinIcon.anchor.set(1, 0.5);
    this.line = new Text({ text: '', style: { ...uiStyle(22, COLOURS.cream), align: 'center' } });
    this.line.anchor.set(0.5, 0);
    this.tagline = new Text({
      text: 'INFINITE COINS\nALL THE SPIN, NONE OF THE STING',
      style: { ...uiStyle(12, 0xf0dcb4, '600'), letterSpacing: 1.2, lineHeight: 17, align: 'center' },
    });
    this.tagline.anchor.set(0.5, 0);
    this.fly = new TakeWinAnimation(Ticker.shared);
    this.addChild(this.dim, this.glow, this.chest, this.shower, this.coinIcon, this.counter, this.line, this.tagline, this.fly);
    this.eventMode = 'none';
    this.visible = false;
  }

  get snapshot(): RefillSnapshot {
    return { playing: this.playing, counter: this.counter.text, line: this.line.text, tagline: this.tagline.text };
  }

  layout(width: number, height: number): void {
    this.w = width;
    this.h = height;
    this.dim.clear().rect(0, 0, width, height).fill({ color: 0x120a03, alpha: 0.88 });
    const cx = width / 2;
    const small = Math.min(width, height) < 560;
    const chestSize = this.chestSize();
    const cy = Math.round(height * (small ? 0.4 : 0.42));
    this.glow.position.set(cx, cy - chestSize * 0.1);
    this.glow.width = this.glow.height = Math.min(width, height) * 1.3;
    const chest = this.art('chest');
    if (chest) this.chest.texture = chest;
    this.chest.width = this.chest.height = chestSize;
    this.chest.position.set(cx, cy - chestSize * 0.25);
    // The counter: the coin, then the number, centred together.
    const size = Math.round(Math.min(84, width * 0.17));
    this.counter.style.fontSize = size;
    const coin = this.art('coin');
    if (coin) this.coinIcon.texture = coin;
    this.coinIcon.width = this.coinIcon.height = Math.round(size * 0.82);
    const counterY = cy + chestSize * 0.5;
    this.centreCounter(counterY);
    this.line.style.fontSize = small ? 20 : 24;
    this.line.style.wordWrap = true;
    this.line.style.wordWrapWidth = width - 48;
    this.line.position.set(cx, counterY + size * 0.62);
    this.tagline.style.wordWrap = true;
    this.tagline.style.wordWrapWidth = width - 48;
    this.tagline.position.set(cx, this.line.y + 40);
  }

  /** Play the moment; resolves when it has gone. */
  async play({ from, amount, target }: RefillPlay): Promise<void> {
    this.playing = true;
    this.visible = true;
    this.alpha = 1;
    this.counter.text = format(from);
    this.line.text = `+${format(amount)} on the house`;
    this.line.alpha = 0;
    this.tagline.alpha = 0;
    this.chest.alpha = 0;
    this.counter.alpha = this.coinIcon.alpha = 0;
    this.layout(this.w, this.h);
    const calm = prefersReducedMotion();

    AudioBus.shared.play('win:sting_medium', { volume: 0.55 });
    const timers: ReturnType<typeof setTimeout>[] = [];
    const at = (ms: number, fn: () => void): void => {
      timers.push(setTimeout(() => !this.destroyed && fn(), ms));
    };
    for (let i = 0; i < 8; i++) at(220 + i * 95, () => AudioBus.shared.play('coin:collect', { volume: 0.22 }));
    at(COUNTER_AT + COUNTER_MS - 200, () => AudioBus.shared.play('bonus:claim', { volume: 0.85 }));

    // In: the dim and the glow, then the chest bursts open.
    void animate(320, (t) => {
      this.dim.alpha = t;
      this.glow.alpha = 0.6 * t;
    });
    at(150, () => {
      void animate(520, (t) => {
        this.chest.alpha = Math.min(1, t * 2);
        const k = 0.4 + 0.6 * ease.outBack(t);
        this.chest.width = this.chest.height = this.chestSize() * k;
      });
      if (!calm) this.burst();
    });
    // The counter rolls up to the new balance.
    at(COUNTER_AT - 200, () => {
      void animate(420, (t) => (this.counter.alpha = this.coinIcon.alpha = t));
    });
    at(COUNTER_AT, () => {
      void animate(COUNTER_MS, (t) => {
        this.counter.text = format(from + amount * ease.outCubic(t));
        this.centreCounter(this.counter.y);
      });
    });
    at(LINE_AT, () => void animate(480, (t) => (this.line.alpha = t)));
    at(TAGLINE_AT, () => void animate(600, (t) => (this.tagline.alpha = 0.85 * t)));
    // The coins fly into the counter as the screen clears.
    at(FLY_AT, () => {
      const to = target();
      if (!to || calm) return;
      void this.fly.takeWin(amount, { x: this.chest.x, y: this.chest.y }, to, () => undefined, {
        maxCoins: 16,
        totalDurationMs: 1000,
        flyDurationMs: 600,
      });
    });
    at(FADE_AT, () => {
      void animate(500, (t) => {
        for (const piece of [this.dim, this.glow, this.chest, this.shower, this.counter, this.coinIcon, this.line, this.tagline]) {
          piece.alpha = Math.min(piece.alpha, 1 - t);
        }
      });
    });
    await new Promise<void>((resolve) => setTimeout(resolve, TOTAL_MS));
    timers.forEach((t) => clearTimeout(t));
    this.stopShower();
    if (!this.destroyed) {
      this.fly.clear();
      this.visible = false;
    }
    this.playing = false;
  }

  override destroy(options?: Parameters<Container['destroy']>[0]): void {
    this.stopShower();
    this.fly.destroyEffect();
    super.destroy(options);
  }

  private chestSize(): number {
    return Math.round(Math.min(280, this.w * 0.5, this.h * 0.32));
  }

  private centreCounter(y: number): void {
    const gap = Math.round(this.counter.height * 0.14);
    const total = this.coinIcon.width + gap + this.counter.width;
    const left = this.w / 2 - total / 2;
    this.coinIcon.position.set(left + this.coinIcon.width, y);
    this.counter.position.set(left + this.coinIcon.width + gap, y);
  }

  /** Coins burst up out of the chest and tumble down. */
  private burst(): void {
    const coin = this.art('coin');
    if (!coin) return;
    const ox = this.chest.x;
    const oy = this.chest.y;
    for (let i = 0; i < COINS; i++) {
      const sprite = new Sprite(coin);
      sprite.anchor.set(0.5);
      const size = 24 + Math.random() * 22;
      sprite.width = sprite.height = size;
      const angle = (Math.random() - 0.5) * Math.PI * 1.4;
      const speed = 420 + Math.random() * 520;
      sprite.position.set(ox + (Math.random() - 0.5) * 80, oy + (Math.random() - 0.5) * 20);
      this.shower.addChild(sprite);
      this.falling.push({
        sprite,
        vx: Math.sin(angle) * speed,
        vy: -Math.cos(angle) * speed - 120,
        spin: (Math.random() - 0.5) * 10,
        age: 0,
        life: 2000 + Math.random() * 600,
      });
    }
    Ticker.shared.add(this.tick);
  }

  private step(ms: number): void {
    const dt = Math.min(48, ms) / 1000;
    this.falling = this.falling.filter((c) => {
      c.age += dt * 1000;
      c.vy += GRAVITY * dt;
      c.sprite.x += c.vx * dt;
      c.sprite.y += c.vy * dt;
      c.sprite.rotation += c.spin * dt;
      const left = 1 - c.age / c.life;
      c.sprite.alpha = Math.max(0, Math.min(1, left * 3));
      if (left <= 0 || c.sprite.y > this.h + 60) {
        c.sprite.destroy();
        return false;
      }
      return true;
    });
    if (this.falling.length === 0) this.stopShower();
  }

  private stopShower(): void {
    Ticker.shared.remove(this.tick);
    for (const c of this.falling) if (!c.sprite.destroyed) c.sprite.destroy();
    this.falling = [];
  }
}
